/**
 * Public-facing marketing content rendered below the existing splash
 * screen on ``/`` and ``/en``. School admins, hagwon admins, and
 * teachers visiting the site scroll past the splash and land here.
 *
 * Scope and structure are pinned by the design brief (Claude Design
 * handoff bundle, 2026-05-02). All copy lives in i18n; this file is
 * structure + interaction only.
 *
 * Sections, in scroll order:
 *   1. Value-proposition / inversion diagram (gradient continues)
 *   2. Post-lecture demonstration moment (stacked header + dual demo)
 *   3. Audience role selector (school / teacher / hagwon tablist)
 *   4. Trust band (PIPA, processing location, transparency)
 *   5. How it works (3 steps)
 *   6. Pilot CTA
 *   7. Footer
 *
 * Several blocks are deliberately commented out and tracked under
 * follow-up issues. Search for the all-caps tags to locate each:
 *
 *   INCORPORATION-PENDING   — issue #182, uncomment when 사업자등록 done
 *   OPERATOR-INFO-PENDING   — footer CPO + registered-office rows gated
 *                             after the previous operator left the
 *                             project; re-enable when a new CPO is
 *                             designated and a new registered address
 *                             exists
 *
 * The commented blocks contain real (or placeholder) markup so that
 * uncommenting is a one-step change. Do NOT delete them.
 *
 * CONSENT-FLOW-DISABLED retired in #185 follow-up. The original
 * plan (D2C-style in-app guardian consent capture) was reshaped in
 * #128 / #441 to a **school-as-controller** posture: institutions
 * discharge Art. 22-2 with guardians at enrollment (off-platform);
 * eehae captures the institution's warranty via the EULA acceptance
 * row and surfaces an Art. 22-2 §3 notice to students at first
 * login. The school-card bullet1 now describes that shipped flow
 * instead of the dropped in-app capture plan.
 */
import { useEffect, useRef, useState } from 'react'
import {
  animate,
  motion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react'
import { Link } from 'react-router-dom'
import { apiPost } from '../api/client'
import { useT, useLang } from '../i18n'
import LanguageSwitcher from './LanguageSwitcher'
// CSS Module — component-internal styles. The seven section roots
// also carry a ``data-marketing-section="N"`` attribute so
// ``snap.css`` and ``App.tsx``'s keyboard handler can target them
// without depending on the (now hashed) class names. See #264 PR B.
import s from './MarketingExtension.module.css'

interface NavLink {
  href: string
  labelKey:
    | 'mkt.nav.problem'
    | 'mkt.nav.audiences'
    | 'mkt.nav.trust'
    | 'mkt.nav.how'
}

const NAV_LINKS: NavLink[] = [
  { href: '#problem', labelKey: 'mkt.nav.problem' },
  { href: '#audiences', labelKey: 'mkt.nav.audiences' },
  { href: '#trust', labelKey: 'mkt.nav.trust' },
  { href: '#how', labelKey: 'mkt.nav.how' },
]

/**
 * Sticky marketing nav that fades in once the splash scrolls out of
 * view. Uses an IntersectionObserver on a sentinel placed at the
 * bottom of the splash so the threshold is independent of the
 * splash's height (which can change with viewport).
 */
function useStickyNavVisible(): boolean {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const sentinel = document.getElementById('mkt-splash-sentinel')
    if (!sentinel) return
    // Direct ``rect.top`` check rather than IntersectionObserver: the
    // sentinel is a zero-height element sitting exactly at the
    // splash/marketing boundary, which is the worst-case edge for
    // IntersectionObserver (some browsers report it as ``isIntersecting``
    // at the boundary, some don't). At the S1 snap target the sentinel
    // lands *exactly* at viewport top, so the IO version left the nav
    // hidden until the next snap nudged scroll a hair further. The
    // 200px lead time triggers the nav reveal partway through the
    // splash→S1 snap so it eases in alongside the scroll instead of
    // popping in at the end.
    const update = () => {
      setVisible(sentinel.getBoundingClientRect().top < 200)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])
  return visible
}

export default function MarketingExtension() {
  const t = useT()
  const navVisible = useStickyNavVisible()
  const choreo = useChoreographyEnabled()
  useChoreoAnchors(choreo)
  useScrollSettle(choreo)

  // While the scroll choreography is active, drop the section-snap
  // regime (snap.css keys off this class) — pinned crossfade stages and
  // proximity snap fight each other.
  useEffect(() => {
    if (!choreo) return
    document.documentElement.classList.add('mkt-choreo')
    return () => document.documentElement.classList.remove('mkt-choreo')
  }, [choreo])

  return (
    <>
      {/* Sentinel for the sticky nav. Sits at the very top of the
          marketing block, immediately below the splash. When this
          is visible (i.e. you're still on the splash), the nav
          stays hidden. When you scroll past it, the nav reveals. */}
      <div id="mkt-splash-sentinel" aria-hidden="true" />

      <StickyNav visible={navVisible} t={t} />

      {choreo ? <ChoreographedSections t={t} /> : <StackedSections t={t} />}
    </>
  )
}

// Plain top-to-bottom layout. The default for touch / short-viewport /
// reduced-motion contexts, and the progressive-enhancement base the
// scroll choreography layers on top of.
// In the stacked / reduced-motion fallback the choreography scroll
// handler that drives ``mkt-footer-on-purple`` never runs, so the
// privacy footer's ``mix-blend-mode: difference`` would re-green over
// the violet sections — the splash gradient and the pilot ``.s6``,
// which share the same ``#3730a3 → #5b21b6 → #7c3aed`` violet. Flag the
// class from real geometry: set it whenever one of those violet
// elements spans the footer strip's vertical band. The cream
// mid-sections and the dark ``.s7`` footer keep the blend (difference
// reads correctly on both); ``.s1`` is excluded because its gradient is
// cream at the bottom, where the strip actually sits. The matching CSS
// override (shared with the choreography path) lives in
// PrivacyFooter.module.css.
function useStackedFooterOnPurple() {
  useEffect(() => {
    const VIOLET_SELECTOR = '.splash, [data-marketing-section="6"]'
    const update = () => {
      // Sample a touch above the strip's 12px baseline so we track the
      // backdrop actually behind the ~12px text, not the viewport edge.
      const stripY = window.innerHeight - 20
      const onPurple = Array.from(
        document.querySelectorAll(VIOLET_SELECTOR),
      ).some((el) => {
        const r = el.getBoundingClientRect()
        return r.top <= stripY && r.bottom >= stripY
      })
      document.documentElement.classList.toggle(
        'mkt-footer-on-purple',
        onPurple,
      )
    }
    update()
    // Re-run once after the next paint as well: the synchronous call
    // above is normally enough (SplashScreen mounts before this), but
    // it guarantees the geometry read happens after layout has settled.
    // Crucially, cancel it on unmount — if a resize switches to
    // ChoreographedSections before the frame fires, a late update()
    // would toggle ``mkt-footer-on-purple`` from stale stacked geometry
    // and stomp the class the choreography handler now owns.
    const rafId = window.requestAnimationFrame(update)
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      document.documentElement.classList.remove('mkt-footer-on-purple')
    }
  }, [])
}

function StackedSections({ t }: { t: ReturnType<typeof useT> }) {
  useStackedFooterOnPurple()
  return (
    <>
      <Section1ValueProp t={t} />
      <Section2PostLecture t={t} />
      <Section3Audiences t={t} />
      <Section4Trust t={t} />
      <Section5HowItWorks t={t} />
      <Section6PilotCta t={t} />
      <Section7Footer t={t} />
    </>
  )
}

// ── Scroll choreography (desktop, motion-allowed) ─────────────────
//
// Replaces the stacked scroll with two pinned crossfade stages and a
// continuous background morph, per the design brief:
//
//   hero ─(scroll)→ S1 ⇢ S2 ⇢ S3   [pinned crossfade; bg gradient→beige]
//        ─(scroll)→ S4 ⇢ S5         [pinned crossfade]
//        ─(scroll; bg beige→purple)→ pilot (top ⅔) + footer (bottom ⅓)
//
// Each ⇢ is an opacity crossfade with a small directional drift (no
// real travel); each ─(scroll)→ is ordinary vertical scroll between the
// stage tracks. Gated by ``useChoreographyEnabled``; StackedSections is
// the reduced-motion / small-screen fallback.

function ChoreographedSections({ t }: { t: ReturnType<typeof useT> }) {
  const stageARef = useRef<HTMLDivElement>(null)
  const stageBRef = useRef<HTMLDivElement>(null)
  const finaleRef = useRef<HTMLDivElement>(null)


  const { scrollYProgress: aP } = useScroll({
    target: stageARef,
    offset: ['start start', 'end end'],
  })
  const { scrollYProgress: bP } = useScroll({
    target: stageBRef,
    offset: ['start start', 'end end'],
  })
  const { scrollYProgress: fP } = useScroll({
    target: finaleRef,
    offset: ['start end', 'start start'],
  })

  // Background choreography. A beige base sits under a single purple
  // overlay that fades in as the finale arrives. There used to be a
  // third "splash-continuing purple gradient" layer above the beige
  // base, but its opacity transition didn't paint reliably in
  // Chromium-based browsers (Firefox fine; Edge held the gradient at
  // full opacity the whole way down, producing the "stuck half-purple"
  // bg). The splash itself already carries a purple→beige gradient at
  // its bottom edge, so the visual handoff from purple hero to beige
  // marketing still happens — it just happens as the splash scrolls
  // past rather than via a separate fixed overlay.
  const purpleOpacity = useTransform(fP, [0.1, 0.85], [0, 1])

  // Sticky-nav colour follows the bg: purple while S1 is the active
  // section (so the nav reads as a continuation of the splash gradient),
  // beige once we cross into S2. The threshold sits at the midpoint
  // between the S1 and S2 snap markers (50% of viewport into stage A).
  // Toggled via a class on <html> that the nav CSS responds to with a
  // 400ms colour transition.
  useEffect(() => {
    const update = () => {
      const y = window.scrollY
      const vh = window.innerHeight
      const splashEnd = vh
      const s2Marker = splashEnd + 0.8 * vh
      const threshold = (splashEnd + s2Marker) / 2
      const onPurple = y >= splashEnd * 0.85 && y < threshold
      document.documentElement.classList.toggle('mkt-nav-on-purple', onPurple)
      // The global privacy/cookie footer strip floats over this same
      // backdrop. It uses ``mix-blend-mode: difference`` for automatic
      // contrast, which reads cleanly on the cream mid-sections
      // (near-black), but inverts violet into a lime-green tint. Two
      // violet runs need flagging: (1) the intro — splash gradient
      // through the S1→S2 threshold (``y < threshold``, broader than
      // the nav's ``onPurple`` window since that starts only *after*
      // the splash); (2) the finale, where ``choreoBgPurple`` fades in
      // over the pilot (``purpleOpacity`` past its midpoint). Over
      // either, PrivacyFooter.module.css drops the blend and renders
      // plain soft-white. The cream run in between keeps the blend.
      const onFooterPurple = y < threshold || purpleOpacity.get() > 0.5
      document.documentElement.classList.toggle(
        'mkt-footer-on-purple',
        onFooterPurple,
      )
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      document.documentElement.classList.remove('mkt-nav-on-purple')
      document.documentElement.classList.remove('mkt-footer-on-purple')
    }
  }, [])

  return (
    <div className={s.choreoRoot}>
      <div className={s.choreoBgBase} aria-hidden="true" />
      <motion.div
        className={s.choreoBgPurple}
        style={{ opacity: purpleOpacity }}
        aria-hidden="true"
      />

      <ProgressIndicator />

      {/* Stage A — S1 ⇢ S2 ⇢ S3. Stage A's track is 300vh: a 100vh
          sticky pin with 200vh of scrub. The last layer's plateau ends
          before progress=1 so it fades out BEFORE the pin un-sticks at
          the bottom of the track. That kills the overlap that otherwise
          happens during the inter-stage scroll — Chromium left both the
          un-sticking pin AND the next sticky pin visible in the same
          viewport during the transition. */}
      <div
        ref={stageARef}
        className={s.choreoTrack}
        data-choreo-track="A"
        style={{ height: '300vh' }}
      >
        <div className={s.choreoPin}>
          <CrossfadeLayer plateauStart={0} plateauEnd={0} progress={aP}>
            <Section1ValueProp t={t} />
          </CrossfadeLayer>
          <CrossfadeLayer
            plateauStart={0.4}
            plateauEnd={0.4}
            progress={aP}
            outToX={-120}
          >
            <Section2PostLecture t={t} />
          </CrossfadeLayer>
          <CrossfadeLayer
            plateauStart={0.8}
            plateauEnd={0.8}
            progress={aP}
            inFromX={120}
            outToY={-100}
          >
            <Section3Audiences t={t} />
          </CrossfadeLayer>
        </div>
        {/* Snap anchors at each plateau's point (point plateaus = the
            crossfade spans the WHOLE inter-marker scroll). Track is
            300vh; progress 0→1 maps to scroll [0, 200vh], so plateau at
            progress p sits at vh = p * 200. */}
        {[0, 80, 160].map((vh) => (
          <span
            key={vh}
            className={s.choreoSnap}
            data-choreo-snap
            style={{ top: `${vh}vh` }}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Stage B — S4 ⇢ S5. Stage B's first layer fades IN after the
          pin has sticked (plateauStart > 0); without that, S4 would
          peek in from below while stage A's pin is still un-sticking. */}
      <div
        ref={stageBRef}
        className={s.choreoTrack}
        data-choreo-track="B"
        style={{ height: '220vh' }}
      >
        <div className={s.choreoPin}>
          <CrossfadeLayer
            plateauStart={0.4}
            plateauEnd={0.4}
            progress={bP}
            inFromY={100}
            outToX={120}
          >
            <Section4Trust t={t} />
          </CrossfadeLayer>
          <CrossfadeLayer
            plateauStart={0.8}
            plateauEnd={0.8}
            progress={bP}
            inFromX={-120}
          >
            <Section5HowItWorks t={t} />
          </CrossfadeLayer>
        </div>
        {/* Stage B track is 220vh; progress 0→1 maps to scroll [0, 120vh]. */}
        {[48, 96].map((vh) => (
          <span
            key={vh}
            className={s.choreoSnap}
            data-choreo-snap
            style={{ top: `${vh}vh` }}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Finale — pilot (top ⅔) + footer (bottom ⅓) */}
      <div ref={finaleRef} className={s.choreoFinale} data-choreo-finale="true">
        <Section6PilotCta t={t} />
        <Section7Footer t={t} />
      </div>
    </div>
  )
}

function CrossfadeLayer({
  plateauStart,
  plateauEnd,
  progress,
  fadeWindow = 0.40,
  inFromX = 0,
  inFromY = 0,
  outToX = 0,
  outToY = 0,
  children,
}: {
  /** Stage progress at which this layer reaches opacity 1. If 0, the
   *  layer is visible from the very start of the stage with no fade-in.
   *  When equal to ``plateauEnd`` the plateau is a single point and the
   *  crossfade between this layer and its neighbours occupies the whole
   *  inter-marker span (slowest, most weighty feel). */
  plateauStart: number
  /** Stage progress at which this layer starts fading back to 0. If 1,
   *  the layer stays visible through the end of the stage with no fade-out. */
  plateauEnd: number
  progress: MotionValue<number>
  fadeWindow?: number
  /** Translation offset (px) at the START of this layer's fade-in. The
   *  layer slides from (inFromX, inFromY) into (0, 0) as it fades in.
   *  Tied to the fade phase, not scroll direction — same code reverses
   *  cleanly when the user scrolls back up. */
  inFromX?: number
  inFromY?: number
  /** Translation offset (px) at the END of this layer's fade-out. The
   *  layer slides from (0, 0) out to (outToX, outToY) as it fades out. */
  outToX?: number
  outToY?: number
  children: React.ReactNode
}) {
  const w = fadeWindow

  // Opacity keyframes. Handles point plateaus (plateauStart ===
  // plateauEnd) cleanly — the fade-in keyframe is pushed only if there's
  // a fade-in zone, and likewise for fade-out, so a point plateau gets a
  // triangular [0, 1, 0] curve instead of failing on duplicate-x
  // keyframes.
  const times: number[] = []
  const opac: number[] = []
  if (plateauStart > 0) {
    times.push(Math.max(0, plateauStart - w))
    opac.push(0)
  }
  times.push(plateauStart)
  opac.push(1)
  if (plateauEnd > plateauStart) {
    times.push(plateauEnd)
    opac.push(1)
  }
  if (plateauEnd < 1) {
    times.push(Math.min(1, plateauEnd + w))
    opac.push(0)
  }
  const opacity = useTransform(progress, times, opac)

  // Directional motion driven by the fade phase. Before plateauStart =
  // fade-in: layer slides from (inFromX, inFromY) → (0, 0). After
  // plateauEnd = fade-out: layer slides from (0, 0) → (outToX, outToY).
  // Inside the plateau (or at the point plateau itself) position is 0.
  const fadeInStart = Math.max(0, plateauStart - w)
  const fadeOutEnd = Math.min(1, plateauEnd + w)
  const x = useTransform(progress, (p) => {
    if (p < plateauStart) {
      if (p <= fadeInStart) return inFromX
      const f = (p - fadeInStart) / (plateauStart - fadeInStart)
      return inFromX * (1 - f)
    }
    if (p > plateauEnd) {
      if (p >= fadeOutEnd) return outToX
      const f = (p - plateauEnd) / (fadeOutEnd - plateauEnd)
      return outToX * f
    }
    return 0
  })
  const y = useTransform(progress, (p) => {
    if (p < plateauStart) {
      if (p <= fadeInStart) return inFromY
      const f = (p - fadeInStart) / (plateauStart - fadeInStart)
      return inFromY * (1 - f)
    }
    if (p > plateauEnd) {
      if (p >= fadeOutEnd) return outToY
      const f = (p - plateauEnd) / (fadeOutEnd - plateauEnd)
      return outToY * f
    }
    return 0
  })

  // Only the (near-)visible layer captures clicks — the absolutely
  // stacked layers above it would otherwise swallow pointer events.
  const pointerEvents = useTransform(opacity, (o) =>
    o < 0.5 ? 'none' : 'auto',
  )
  // ``visibility: hidden`` once a layer is essentially invisible: this
  // is belt-and-suspenders for Chromium-based browsers (Edge in
  // particular) where the opacity-zero layer would intermittently keep
  // painting on top of the next layer. With visibility flipped to
  // hidden, the browser is told unambiguously to skip rendering. The
  // 0.02 threshold is well below the eye's discrimination floor so
  // there is no visible pop.
  const visibility = useTransform(opacity, (o) =>
    o < 0.02 ? 'hidden' : 'visible',
  )

  return (
    <motion.div
      className={s.choreoLayer}
      style={{ opacity, x, y, visibility, pointerEvents }}
    >
      {children}
    </motion.div>
  )
}

// Enable the choreography only where pinned full-viewport crossfades
// behave: pointer/large viewports tall enough to hold a section, and
// only when the visitor hasn't asked for reduced motion. Everything
// else gets StackedSections.
// Test-time escape hatch. Playwright's visual project sets this flag
// via ``addInitScript`` so the visual baselines capture the static
// stacked layout instead of the dynamic choreography (whose
// opacity-tied sections trip ``scrollIntoViewIfNeeded``). Set before
// the React tree mounts; checked synchronously by the gate so the
// initial render already lands on ``StackedSections``.
declare global {
  interface Window {
    __forceStackedMarketing__?: boolean
  }
}

function useChoreographyEnabled(): boolean {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    // Test-time override wins outright.
    if (window.__forceStackedMarketing__) {
      setEnabled(false)
      return
    }
    // Three runtime gates: large viewport, fine-pointer input (so
    // wheel / arrow keys are the dominant scroll modality), AND no
    // reduced-motion preference. The reduced-motion check uses
    // ``matchMedia`` directly rather than Motion's
    // ``useReducedMotion`` because the latter can return ``null``
    // before its internal media-query listener fires, and
    // ``!null === true`` would leave choreography enabled on a
    // reduced-motion visitor.
    //
    // Touch *capability* alone no longer disqualifies. #1083 also
    // gated on ``maxTouchPoints === 0``, which excluded every
    // touchscreen laptop even when driven by a trackpad / mouse —
    // the common Windows-hybrid case, where the choreography then
    // silently never appeared. ``(hover: hover) and (pointer: fine)``
    // already establishes that the *primary* input is a mouse, so a
    // pure tablet / phone (coarse primary pointer) still falls back
    // to StackedSections. On a mouse-primary hybrid, occasional
    // touch-drag scrolling degrades gracefully: native scroll still
    // drives the scrollY-tied crossfade; only the one-gesture-per-
    // section snap (wheel / keyboard only) is absent.
    const sizeMq = window.matchMedia(
      '(min-width: 1024px) and (min-height: 720px)',
    )
    const finePointerMq = window.matchMedia(
      '(hover: hover) and (pointer: fine)',
    )
    const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () =>
      setEnabled(
        sizeMq.matches && finePointerMq.matches && !reduceMq.matches,
      )
    update()
    sizeMq.addEventListener('change', update)
    finePointerMq.addEventListener('change', update)
    reduceMq.addEventListener('change', update)
    return () => {
      sizeMq.removeEventListener('change', update)
      finePointerMq.removeEventListener('change', update)
      reduceMq.removeEventListener('change', update)
    }
  }, [])
  return enabled
}

// Where each in-page anchor should land while the choreography is
// active. Section ids live on absolutely-positioned layers inside the
// pinned stages, so native ``#hash`` scrolling would jump to the top of
// the whole stage; instead we scroll to the point in the stage's track
// where that section is the active crossfade frame.
const CHOREO_ANCHOR_TARGETS: Record<
  string,
  { track?: 'A' | 'B'; p?: number; finale?: boolean }
> = {
  // Progress values match the section plateau centres set in
  // ChoreographedSections. The previous values pointed at fade-zone
  // positions, which dropped anchor-click users onto half-faded
  // sections — reported as "links go to blank pages or half-faded
  // states".
  '#problem': { track: 'A', p: 0 },
  '#audiences': { track: 'A', p: 0.8 },
  '#trust': { track: 'B', p: 0.4 },
  '#how': { track: 'B', p: 0.8 },
  '#pilot': { finale: true },
}

function useChoreoAnchors(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    // Compute the doc scroll target for a given hash. Used by click
    // intercepts, the initial-hash redirect on mount, and the
    // ``hashchange`` event so deep links + browser back/forward all
    // land on the right plateau. CodeRabbit caught the missing
    // hashchange/initial-hash paths on the initial PR.
    const scrollTopForHash = (hash: string): number | null => {
      const target = CHOREO_ANCHOR_TARGETS[hash]
      if (!target) return null
      if (target.finale) {
        const el = document.querySelector('[data-choreo-finale]')
        if (!el) return null
        return Math.round(window.scrollY + el.getBoundingClientRect().top)
      }
      if (target.track) {
        const el = document.querySelector(
          `[data-choreo-track="${target.track}"]`,
        ) as HTMLElement | null
        if (!el) return null
        const trackTop = window.scrollY + el.getBoundingClientRect().top
        const scrollable = Math.max(0, el.offsetHeight - window.innerHeight)
        return Math.round(trackTop + (target.p ?? 0) * scrollable)
      }
      return null
    }
    const goToHash = (hash: string) => {
      const top = scrollTopForHash(hash)
      if (top == null) return
      // Use the shared scroll spring so anchor jumps land with the
      // same feel as wheel-driven snaps (and any in-flight snap is
      // cancelled in favour of the new target).
      springScrollTo(window.scrollY, top)
    }
    const handler = (e: MouseEvent) => {
      // Bail on modifier-augmented clicks (open in new tab, etc.) so
      // the browser's default behaviour wins.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
        return
      const anchor = (e.target as HTMLElement | null)?.closest('a[href^="#"]')
      if (!anchor) return
      const hash = anchor.getAttribute('href') || ''
      if (!(hash in CHOREO_ANCHOR_TARGETS)) return
      e.preventDefault()
      // Update the URL so back/forward + share-this-link both work
      // without re-running the whole anchor path on history pop.
      if (window.location.hash !== hash) {
        history.replaceState(null, '', hash)
      }
      goToHash(hash)
    }
    const onHashChange = () => {
      const hash = window.location.hash
      if (hash) goToHash(hash)
    }
    document.addEventListener('click', handler)
    window.addEventListener('hashchange', onHashChange)
    // Honour an initial hash on first paint (deferred one frame so
    // the choreo tracks have laid out and ``scrollTopForHash`` can
    // measure them).
    const rafId = window.requestAnimationFrame(() => {
      if (window.location.hash) onHashChange()
    })
    return () => {
      document.removeEventListener('click', handler)
      window.removeEventListener('hashchange', onHashChange)
      window.cancelAnimationFrame(rafId)
    }
  }, [enabled])
}

// ── Progress indicator ───────────────────────────────────────────
//
// Vertical rail on the right side with one dot per snap marker
// (subway-map style). The active dot is enlarged with a soft glow,
// and the size transition has a touch of overshoot so the active
// state pops into place rather than easing in linearly. Clicking a
// dot snaps to that section via the shared scroll spring, so it
// reuses exactly the same timing/feel as wheel-driven snaps.
// Per-marker label keys in the order the markers appear (splash, S1,
// S2, S3, S4, S5, pilot). Pulled from i18n so assistive tech reads
// the actual section name in the visitor's locale instead of a
// generic "Section N". Existing ``mkt.nav.*`` keys are reused where
// they exist; ``mkt.progress.top`` and ``mkt.progress.demo`` are
// new keys added for the splash and S2 (which have no nav entry).
const PROGRESS_LABEL_KEYS: Parameters<ReturnType<typeof useT>>[0][] = [
  'mkt.progress.top',
  'mkt.nav.problem',
  'mkt.progress.demo',
  'mkt.nav.audiences',
  'mkt.nav.trust',
  'mkt.nav.how',
  'mkt.nav.cta',
]

function ProgressIndicator() {
  const t = useT()
  const [markers, setMarkers] = useState<number[]>([])
  const [activeIdx, setActiveIdx] = useState(0)

  // Marker positions in document coordinates depend on viewport height
  // (the tracks size themselves in vh), so recompute on resize.
  useEffect(() => {
    const refresh = () => setMarkers(collectChoreoMarkers())
    // Initial collection is deferred one frame so the choreography's
    // markers are already in the DOM when this runs.
    const id = window.requestAnimationFrame(refresh)
    window.addEventListener('resize', refresh)
    return () => {
      window.cancelAnimationFrame(id)
      window.removeEventListener('resize', refresh)
    }
  }, [])

  useEffect(() => {
    if (markers.length === 0) return
    const update = () => {
      const y = window.scrollY
      let best = 0
      let bestDist = Math.abs(markers[0] - y)
      for (let i = 1; i < markers.length; i++) {
        const d = Math.abs(markers[i] - y)
        if (d < bestDist) {
          best = i
          bestDist = d
        }
      }
      setActiveIdx(best)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [markers])

  const goTo = (idx: number) => {
    if (idx < 0 || idx >= markers.length) return
    springScrollTo(window.scrollY, markers[idx])
  }

  if (markers.length === 0) return null

  return (
    <div
      className={s.progressBar}
      role="navigation"
      aria-label="Page sections"
    >
      {markers.map((_, i) => {
        const labelKey = PROGRESS_LABEL_KEYS[i]
        const label = labelKey ? t(labelKey) : `Section ${i + 1}`
        return (
          <button
            key={i}
            type="button"
            className={[s.progressDot, i === activeIdx && s.progressDotActive]
              .filter(Boolean)
              .join(' ')}
            onClick={() => goTo(i)}
            aria-label={label}
            title={label}
            aria-current={i === activeIdx ? 'true' : undefined}
          />
        )
      })}
    </div>
  )
}

// Section-snap in JS. CSS scroll-snap proved unreliable over the pinned
// stages (Chromium gets "stuck" fighting the sticky tracks; Firefox never
// engages), so we drive snapping ourselves: each discrete wheel gesture /
// arrow-key press eases scroll to the next snap marker in the input's
// direction via a shared spring. Native scroll is blocked
// (preventDefault) while the choreography is mounted, so the spring never
// fights browser inertia, and a fresh opposite-direction input cancels and
// reverses an in-flight snap so it never fights the user. Velocity is read
// from Motion's scroll value to give the spring a natural starting speed.
// Single source of truth for the scroll spring. The wheel-driven snap,
// anchor-link clicks, and progress-indicator dot clicks all funnel
// through here so they share one in-flight animation and one set of
// spring params. Tracking the target lets the wheel handler ask "is an
// animation in progress, and which direction is it heading" without
// each caller needing its own per-trigger state.
let activeScrollAnim: ReturnType<typeof animate> | null = null
let activeScrollTarget: number | null = null

// Wall-clock (ms) and direction of the most recently *completed* snap.
// For a short window after a snap settles, same-direction wheel input is
// ignored (see ``advance``). A trackpad keeps emitting wheel events — its
// inertial momentum tail — for a beat after the user lifts their fingers;
// without this, that tail fires a second ``advance`` the instant the spring
// completes and carries the user an unwanted extra section past where they
// landed. The cooldown is a fixed timer, so a *sustained* scroll just
// resumes advancing once it expires (the page still moves section-by-section
// under a held gesture); only the decaying tail of a discrete flick is
// swallowed. A direction reversal is never blocked.
let lastSettleTs = -Infinity
let lastSettleDir: 1 | -1 = 1
const SETTLE_COOLDOWN_MS = 350

function springScrollTo(
  from: number,
  to: number,
  opts: { velocity?: number } = {},
): void {
  if (activeScrollAnim) {
    activeScrollAnim.stop()
    activeScrollAnim = null
    activeScrollTarget = null
  }
  if (Math.abs(to - from) < 2) return
  activeScrollTarget = to
  const dir: 1 | -1 = to > from ? 1 : -1
  activeScrollAnim = animate(from, to, {
    type: 'spring',
    stiffness: 110,
    damping: 21,
    mass: 1,
    velocity: opts.velocity ?? 0,
    restDelta: 1,
    restSpeed: 10,
    onUpdate: (v) => window.scrollTo(0, v),
    onComplete: () => {
      activeScrollAnim = null
      activeScrollTarget = null
      // Open the post-settle cooldown that absorbs this gesture's
      // momentum tail (see the ``lastSettleTs`` declaration).
      lastSettleTs = performance.now()
      lastSettleDir = dir
    },
  })
}

function collectChoreoMarkers(): number[] {
  const cur = window.scrollY
  const markers: number[] = [0]
  // Markers are tagged with ``data-choreo-snap`` rather than picked
  // by CSS-module class name. The original selector
  // ``[class*="choreoSnap"]`` relied on Vite's CSS-modules hash format
  // keeping the literal "choreoSnap" substring in the hashed name —
  // which works in dev but breaks under a hash-only production build,
  // silently collapsing the marker list to ``[0]`` and breaking snap
  // navigation + the progress indicator. Data-attribute selectors
  // are minification-proof. CodeRabbit (outside-diff) on PR #1083.
  document
    .querySelectorAll('[data-choreo-snap], [data-choreo-finale]')
    .forEach((el) =>
      markers.push(Math.round(cur + el.getBoundingClientRect().top)),
    )
  return [...new Set(markers)].sort((a, b) => a - b)
}

function useScrollSettle(enabled: boolean) {
  const { scrollY } = useScroll()

  // Each wheel gesture / arrow-key press eases ONE snap toward the next
  // section in the input's direction. preventDefault on the input event
  // blocks the browser's native scroll entirely, so there's no drift
  // fighting our spring. Two guards keep a single gesture to a single
  // section: (1) while a snap is in flight, same-direction input is
  // redundant and ignored (opposite-direction input falls through to
  // cancel and reverse); (2) for a brief cooldown *after* a snap settles,
  // same-direction input is ignored so a trackpad's inertial momentum tail
  // can't carry us an extra section. A sustained scroll resumes advancing
  // the moment the cooldown expires, so the page still moves
  // section-by-section under a held gesture.
  const advance = (dir: 1 | -1) => {
    if (activeScrollAnim != null && activeScrollTarget != null) {
      const animDir = activeScrollTarget > window.scrollY ? 1 : -1
      if (animDir === dir) return
    } else if (
      dir === lastSettleDir &&
      performance.now() - lastSettleTs < SETTLE_COOLDOWN_MS
    ) {
      // Within the post-settle cooldown, still heading the same way: the
      // momentum tail of the gesture that produced the last snap, not a
      // fresh push. Swallow it so we don't overshoot the section we landed
      // on. (Reversals skip this branch and stay responsive.)
      return
    }
    const cur = window.scrollY
    const uniq = collectChoreoMarkers()
    const EPS = 6
    const target =
      dir > 0
        ? uniq.find((m) => m > cur + EPS)
        : [...uniq].reverse().find((m) => m < cur - EPS)
    if (target == null) return
    springScrollTo(cur, target, { velocity: scrollY.getVelocity() })
  }

  useEffect(() => {
    if (!enabled) return
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= 4) return
      e.preventDefault()
      advance(e.deltaY > 0 ? 1 : -1)
    }
    const onKey = (e: KeyboardEvent) => {
      // Don't hijack keys when the focus is on an interactive control:
      // Space should activate buttons (including the audience-tablist
      // tabs and progress-indicator dots), arrow keys should move
      // through tab lists, Home/End should work inside text inputs.
      // CodeRabbit caught this on the initial PR.
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.closest(
          'a, button, input, textarea, select, [role="tab"], [contenteditable="true"]',
        ) ||
          target.isContentEditable)
      ) {
        return
      }
      if (['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar'].includes(e.key)) {
        e.preventDefault()
        advance(1)
      } else if (['ArrowUp', 'PageUp', 'Home'].includes(e.key)) {
        e.preventDefault()
        advance(-1)
      }
    }
    // ``passive: false`` so we can preventDefault and block the
    // browser's own scroll behaviour. Scoped to the choreography
    // surface (the effect only runs when enabled = true), so other
    // routes keep native scroll.
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKey)
      if (activeScrollAnim) {
        activeScrollAnim.stop()
        activeScrollAnim = null
        activeScrollTarget = null
      }
    }
  }, [enabled])
}

// ── Sticky nav ────────────────────────────────────────────────────

interface NavProps {
  visible: boolean
  t: ReturnType<typeof useT>
}

function StickyNav({ visible, t }: NavProps) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  return (
    <nav
      className={[s.nav, visible && s.isVisible].filter(Boolean).join(' ')}
      aria-label="Site navigation"
    >
      <button
        type="button"
        className={s.navLogo}
        onClick={scrollToTop}
        aria-label="eehae"
      >
        eehae
      </button>
      <div className={s.navLinks}>
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href}>
            {t(link.labelKey)}
          </a>
        ))}
        <a href="#pilot" className={s.navCta}>
          {t('mkt.nav.cta')}
        </a>
      </div>
    </nav>
  )
}

// ── Section 1 — value proposition / inversion diagram ─────────────

function Section1ValueProp({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s1} data-marketing-section="1" id="problem">
      <div className={s.s1Inner}>
        <div className={`${s.eyebrow} ${s.eyebrowOnGradient}`}>
          {t('mkt.s1.eyebrow')}
        </div>
        <h2 className={s.s1Headline}>
          {t('mkt.s1.headline.line1')}
          <br />
          {t('mkt.s1.headline.line2')}{' '}
          <span className={s.accent}>{t('mkt.s1.headline.accent')}</span>
          {t('mkt.s1.headline.line3')}
        </h2>
        <p className={s.s1Body}>{t('mkt.s1.body')}</p>

        <div className={s.diagram}>
          <DiagramSide
            label={t('mkt.s1.diagram.beforeLabel')}
            titleLine1={t('mkt.s1.diagram.beforeTitle.line1')}
            titleLine2={t('mkt.s1.diagram.beforeTitle.line2')}
            svg={<DiagramBefore teacherLabel={t('mkt.s1.diagram.teacher')} />}
          />
          <div className={s.diagramArrow} aria-hidden="true">
            <span className={s.diagramArrowText}>
              {t('mkt.s1.diagram.invertLabel')}
            </span>
          </div>
          <DiagramSide
            label={t('mkt.s1.diagram.afterLabel')}
            titleLine1={t('mkt.s1.diagram.afterTitle.line1')}
            titleLine2={t('mkt.s1.diagram.afterTitle.line2')}
            svg={
              <DiagramAfter
                teacherLabel={t('mkt.s1.diagram.teacher')}
                teacherUnderLabel={t('mkt.s1.diagram.teacherUnder')}
                studentsUnderLabel={t('mkt.s1.diagram.studentsUnder')}
                lecturingLabel={t('mkt.s1.diagram.lecturing')}
                tutoringLabel={t('mkt.s1.diagram.tutoring')}
              />
            }
          />
        </div>
      </div>
    </section>
  )
}

interface DiagramSideProps {
  label: string
  titleLine1: string
  titleLine2: string
  svg: React.ReactNode
}

function DiagramSide({ label, titleLine1, titleLine2, svg }: DiagramSideProps) {
  return (
    <div className={s.diagramSide}>
      <div className={s.diagramLabel}>{label}</div>
      {svg}
      <div className={s.diagramTitle}>
        {titleLine1}
        <br />
        {titleLine2}
      </div>
    </div>
  )
}

function DiagramBefore({ teacherLabel }: { teacherLabel: string }) {
  // Stylized SVG: one teacher in the center connected to many
  // student dots arranged in a circle around them. Self-contained;
  // colors come from currentColor + hardcoded brand tokens since
  // the SVG sits on the gradient background.
  return (
    <svg
      viewBox="0 0 320 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={s.diagramSvg}
    >
      <circle cx="160" cy="120" r="22" fill="#fde68a" />
      <text
        x="160"
        y="125"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="11"
        fontWeight="600"
        fill="#5b21b6"
      >
        {teacherLabel}
      </text>
      <g stroke="rgba(255,255,255,0.35)" strokeWidth="1">
        <line x1="160" y1="120" x2="48" y2="48" />
        <line x1="160" y1="120" x2="160" y2="36" />
        <line x1="160" y1="120" x2="272" y2="48" />
        <line x1="160" y1="120" x2="36" y2="120" />
        <line x1="160" y1="120" x2="284" y2="120" />
        <line x1="160" y1="120" x2="48" y2="192" />
        <line x1="160" y1="120" x2="160" y2="204" />
        <line x1="160" y1="120" x2="272" y2="192" />
      </g>
      <g fill="rgba(255,255,255,0.85)">
        <circle cx="48" cy="48" r="10" />
        <circle cx="160" cy="36" r="10" />
        <circle cx="272" cy="48" r="10" />
        <circle cx="36" cy="120" r="10" />
        <circle cx="284" cy="120" r="10" />
        <circle cx="48" cy="192" r="10" />
        <circle cx="160" cy="204" r="10" />
        <circle cx="272" cy="192" r="10" />
      </g>
    </svg>
  )
}

function DiagramAfter({
  teacherLabel,
  teacherUnderLabel,
  studentsUnderLabel,
  lecturingLabel,
  tutoringLabel,
}: {
  teacherLabel: string
  teacherUnderLabel: string
  studentsUnderLabel: string
  lecturingLabel: string
  tutoringLabel: string
}) {
  // Stylized SVG: the teacher (left, gold) lectures the same column
  // of students (center) that eehae (right, violet) provides parallel
  // 1:1 tutoring to. Two top labels — LECTURING above the teacher's
  // arrows, 1:1 TUTORING above eehae's — frame the dual-channel
  // framing: eehae sits *alongside* the teacher's lecturing role
  // rather than replacing it. Adapted from the Claude Design
  // "Elevator Pitch" with-eehae SVG.
  return (
    <svg
      viewBox="0 0 320 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={s.diagramSvg}
    >
      <defs>
        <marker
          id="diagramAfterArrYel"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0,1 L6,4 L0,7 Z" fill="rgba(253,230,138,0.92)" />
        </marker>
        <marker
          id="diagramAfterArrVio"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          {/* Light lavender (~Tailwind violet-200) so the arrowheads
              read against the s1 purple gradient — violet-300 was
              still too close in hue to the background to pop. The
              eehae node keeps its darker violet-400 fill as the
              saturated anchor; arrows lighten as they travel toward
              students, creating clear figure-ground separation. */}
          <path d="M0,1 L6,4 L0,7 Z" fill="rgba(221,214,254,1)" />
        </marker>
      </defs>

      {/* Top labels framing each side's role */}
      <text
        x="95"
        y="18"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="10"
        fill="rgba(253,230,138,0.92)"
        letterSpacing="2"
        fontWeight="600"
      >
        {lecturingLabel}
      </text>
      <text
        x="220"
        y="18"
        textAnchor="middle"
        fontFamily="JetBrains Mono"
        fontSize="10"
        fill="rgba(167,139,250,0.98)"
        letterSpacing="2"
        fontWeight="600"
      >
        {tutoringLabel}
      </text>

      {/* Brackets under top labels */}
      <path
        d="M 40 26 L 40 32 L 150 32 L 150 26"
        stroke="rgba(253,230,138,0.45)"
        strokeWidth="1"
        fill="none"
      />
      <path
        d="M 170 26 L 170 32 L 280 32 L 280 26"
        stroke="rgba(167,139,250,0.5)"
        strokeWidth="1"
        fill="none"
      />

      {/* Teacher node (left, vertically centered on the student column) */}
      <circle cx="22" cy="120" r="15" fill="#fde68a" />
      <text
        x="22"
        y="125"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="13"
        fontWeight="700"
        fill="#5b21b6"
      >
        {teacherLabel}
      </text>
      <text
        x="22"
        y="200"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="9"
        fill="rgba(255,255,255,0.78)"
        fontWeight="600"
        letterSpacing="1.5"
      >
        {teacherUnderLabel}
      </text>

      {/* Teacher → students (gold lecture arrows, fanned to the column) */}
      <g stroke="rgba(253,230,138,0.7)" strokeWidth="1.3" fill="none">
        <line x1="37" y1="120" x2="150" y2="60" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="90" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="120" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="150" markerEnd="url(#diagramAfterArrYel)" />
        <line x1="37" y1="120" x2="150" y2="180" markerEnd="url(#diagramAfterArrYel)" />
      </g>

      {/* 5 students in a vertical column */}
      <g fill="rgba(255,255,255,0.94)">
        <circle cx="160" cy="60" r="9" />
        <circle cx="160" cy="90" r="9" />
        <circle cx="160" cy="120" r="9" />
        <circle cx="160" cy="150" r="9" />
        <circle cx="160" cy="180" r="9" />
      </g>
      <text
        x="160"
        y="200"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="9"
        fill="rgba(255,255,255,0.78)"
        fontWeight="600"
        letterSpacing="1.5"
      >
        {studentsUnderLabel}
      </text>

      {/* eehae → students (violet 1:1 tutoring arrows, fanned from the right) */}
      <g stroke="rgba(221,214,254,0.95)" strokeWidth="1.3" fill="none">
        <line x1="283" y1="120" x2="170" y2="60" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="90" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="120" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="150" markerEnd="url(#diagramAfterArrVio)" />
        <line x1="283" y1="120" x2="170" y2="180" markerEnd="url(#diagramAfterArrVio)" />
      </g>

      {/* eehae node (right) */}
      <rect
        x="283"
        y="100"
        width="32"
        height="40"
        rx="9"
        fill="rgba(167,139,250,0.98)"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="0.7"
      />
      <text
        x="299"
        y="124"
        textAnchor="middle"
        fontFamily="Outfit"
        fontSize="11"
        fontWeight="650"
        fill="#fff"
        letterSpacing="-0.2"
      >
        eehae
      </text>
    </svg>
  )
}

// ── Section 2 — post-lecture demonstration moment ─────────────────

function Section2PostLecture({ t }: { t: ReturnType<typeof useT> }) {
  // Pulse mockup uses real /api/classes/{code}/pulse field labels +
  // illustrative-but-plausible numbers. NOT real components — this is
  // a static marketing illustration that happens to mirror the real
  // dashboard's metric shape so the visual matches what teachers
  // would actually see post-pilot.
  return (
    <section className={s.s2} data-marketing-section="2">
      <div className={s.s2Inner}>
        <div className={s.s2Header}>
          <h2 className={s.s2Headline}>{t('mkt.s2.headline')}</h2>
          <div className={s.s2Body}>
            <p>
              {t('mkt.s2.body.p1.lead')}
              <strong>{t('mkt.s2.body.p1.strong')}</strong>
            </p>
            <p>{t('mkt.s2.body.p2')}</p>
            <p>{t('mkt.s2.body.p3')}</p>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitArrow} aria-hidden="true">
            →
          </div>

          {/* Student-side panel */}
          <div className={s.panel}>
            <div className={s.panelTag}>{t('mkt.s2.studentTag')}</div>
            <div className={s.panelTitle}>{t('mkt.s2.studentPanelTitle')}</div>
            <div className={s.chatMock}>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai1')}</div>
              <div className={`${s.chatBubble} ${s.student}`}>
                {t('mkt.s2.chat.student1')}
              </div>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai2')}</div>
              <div className={`${s.chatBubble} ${s.student}`}>
                {t('mkt.s2.chat.student2')}
              </div>
              <div className={`${s.chatBubble} ${s.ai}`}>{t('mkt.s2.chat.ai3')}</div>
              <div className={s.chatMeta}>{t('mkt.s2.chat.meta')}</div>
            </div>
          </div>

          {/* Teacher-side panel */}
          <div className={s.panel}>
            <div className={s.panelTag}>{t('mkt.s2.teacherTag')}</div>
            <div className={s.panelTitle}>
              {t('mkt.s2.teacherPanelTitle')} ·{' '}
              <span className={s.livePill}>{t('mkt.s2.live')}</span>
            </div>
            <div className={s.dashMock}>
              <div className={s.dashPulseRow}>
                <PulseStat
                  label={t('mkt.s2.pulse.active')}
                  value={t('mkt.s2.pulse.activeValue')}
                  variant="default"
                />
                <PulseStat
                  label={t('mkt.s2.pulse.sessions')}
                  value={t('mkt.s2.pulse.sessionsValue')}
                  variant="default"
                />
                <PulseStat
                  label={t('mkt.s2.pulse.mastery')}
                  value={t('mkt.s2.pulse.masteryValue')}
                  variant="default"
                />
              </div>
              <div className={s.dashAttentionRow}>
                <span className={s.attentionLabel}>
                  {t('mkt.s2.pulse.attention')}
                </span>
                <span className={s.attentionNum}>
                  {t('mkt.s2.pulse.attentionValue')}
                </span>
                <span className={s.attentionUnit}>
                  {t('mkt.s2.pulse.attentionUnit')}
                </span>
                <span className={s.attentionAvatars} aria-hidden="true">
                  <span className={s.attentionAvatar}>민</span>
                  <span className={s.attentionAvatar}>서</span>
                  <span className={s.attentionAvatar}>준</span>
                  <span className={s.attentionAvatar}>윤</span>
                </span>
              </div>
              {/* Mini sparkline placeholder. Real dashboard renders a
                  Sparkline component over sessions_per_day; here we
                  hardcode a path so the page is self-contained. */}
              <div className={s.dashSparkline} aria-hidden="true">
                <svg viewBox="0 0 200 32" preserveAspectRatio="none">
                  <polyline
                    points="0,24 28,20 56,22 84,12 112,16 140,8 168,14 200,6"
                    fill="none"
                    stroke="var(--violet-600, #7c3aed)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

interface PulseStatProps {
  label: string
  value: string
  variant?: 'default' | 'ok' | 'warn' | 'bad'
}

function PulseStat({ label, value, variant = 'default' }: PulseStatProps) {
  // Map BEM variant to the module class. Pre-#264 PR B this was
  // ``mkt-pulse-stat-num--${variant}`` interpolated directly; under
  // CSS Modules the class names are hashed so we look them up via
  // the module object.
  const variantClass = {
    default: s.pulseStatNumDefault,
    ok: s.pulseStatNumOk,
    warn: s.pulseStatNumWarn,
    bad: s.pulseStatNumBad,
  }[variant]
  return (
    <div className={s.pulseStat}>
      <div className={s.pulseStatLabel}>{label}</div>
      <div className={`${s.pulseStatNum} ${variantClass}`}>{value}</div>
    </div>
  )
}

// ── Section 3 — three audiences ───────────────────────────────────

interface AudienceCardData {
  variant: 'school' | 'teacher' | 'hagwon'
  icon: React.ReactNode
}

const AUDIENCE_CARDS: AudienceCardData[] = [
  { variant: 'school', icon: <SchoolIcon /> },
  { variant: 'teacher', icon: <TeacherIcon /> },
  { variant: 'hagwon', icon: <HagwonIcon /> },
]

function Section3Audiences({ t }: { t: ReturnType<typeof useT> }) {
  // Role selector — the three audiences (school / teacher / hagwon)
  // share one panel that swaps on tab select instead of sitting in
  // three side-by-side cards. Denser, and it lets a visitor jump
  // straight to their own context. Implemented as an ARIA tablist
  // (arrow-key navigable); every panel stays in the DOM and toggles
  // via the ``hidden`` attribute, so nothing is hidden from crawlers.
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const next = (active + dir + AUDIENCE_CARDS.length) % AUDIENCE_CARDS.length
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section className={s.s3} data-marketing-section="3" id="audiences">
      <div className={s.s3Inner}>
        <h2 className={s.s3Headline}>{t('mkt.s3.headline')}</h2>

        <div className={s.audiencePicker}>
          <div
            className={s.audienceTabs}
            role="tablist"
            aria-label={t('mkt.nav.audiences')}
            onKeyDown={onKeyDown}
          >
            {AUDIENCE_CARDS.map((card, i) => {
              const labelKey =
                `mkt.s3.${card.variant}.label` as Parameters<typeof t>[0]
              return (
                <button
                  key={card.variant}
                  ref={(el) => {
                    tabRefs.current[i] = el
                  }}
                  type="button"
                  role="tab"
                  id={`audience-tab-${card.variant}`}
                  aria-selected={active === i}
                  aria-controls={`audience-panel-${card.variant}`}
                  tabIndex={active === i ? 0 : -1}
                  className={[s.audienceTab, active === i && s.audienceTabActive]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setActive(i)}
                >
                  <span className={s.audienceTabIcon} aria-hidden="true">
                    {card.icon}
                  </span>
                  {t(labelKey)}
                </button>
              )
            })}
          </div>

          {AUDIENCE_CARDS.map((card, i) => {
            const k = (suffix: string) =>
              `mkt.s3.${card.variant}.${suffix}` as Parameters<typeof t>[0]
            return (
              <div
                key={card.variant}
                role="tabpanel"
                id={`audience-panel-${card.variant}`}
                aria-labelledby={`audience-tab-${card.variant}`}
                // ``tabIndex={0}`` on the active panel only — the
                // hidden siblings don't render any focusable content
                // and skipping them keeps the tab order clean. With
                // this, keyboard users who select a tab in the strip
                // can tab forward INTO the panel content (static
                // copy + bullets), matching the ARIA Authoring
                // Practices Guide pattern for static-content tabs.
                // CodeRabbit (outside-diff) on PR #1083.
                tabIndex={active === i ? 0 : -1}
                hidden={active !== i}
                className={s.audiencePanel}
              >
                <h3 className={s.audiencePanelHeadline}>{t(k('headline'))}</h3>
                <ul className={s.audiencePanelBullets}>
                  <li>{t(k('bullet1'))}</li>
                  <li>{t(k('bullet2'))}</li>
                  <li>{t(k('bullet3'))}</li>
                  <li>{t(k('bullet4'))}</li>
                </ul>
                <p className={s.audiencePanelFit}>
                  <span className={s.audienceFitLabel}>{t(k('fitLabel'))}</span>
                  {t(k('fit'))}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// ── Section 4 — trust pillars ─────────────────────────────────────

interface TrustPillarData {
  icon: React.ReactNode
  titleKey: 'mkt.s4.pillar1.title' | 'mkt.s4.pillar2.title' | 'mkt.s4.pillar3.title'
  subtitleKey:
    | 'mkt.s4.pillar1.subtitle'
    | 'mkt.s4.pillar2.subtitle'
    | 'mkt.s4.pillar3.subtitle'
  bodyKey: 'mkt.s4.pillar1.body' | 'mkt.s4.pillar2.body' | 'mkt.s4.pillar3.body'
}

const TRUST_PILLARS: TrustPillarData[] = [
  {
    icon: <ShieldIcon />,
    titleKey: 'mkt.s4.pillar1.title',
    subtitleKey: 'mkt.s4.pillar1.subtitle',
    bodyKey: 'mkt.s4.pillar1.body',
  },
  {
    icon: <GlobeIcon />,
    titleKey: 'mkt.s4.pillar2.title',
    subtitleKey: 'mkt.s4.pillar2.subtitle',
    bodyKey: 'mkt.s4.pillar2.body',
  },
  {
    icon: <DocumentIcon />,
    titleKey: 'mkt.s4.pillar3.title',
    subtitleKey: 'mkt.s4.pillar3.subtitle',
    bodyKey: 'mkt.s4.pillar3.body',
  },
]

function Section4Trust({ t }: { t: ReturnType<typeof useT> }) {
  // Three trust guarantees grouped into one bordered band split by
  // hairlines, rather than three free-standing cards. Reads as a
  // single compact unit and drops the decorative 01/02/03 numbering.
  return (
    <section className={s.s4} data-marketing-section="4" id="trust">
      <div className={s.s4Inner}>
        <div className={s.s4Header}>
          <h2 className={s.s4Headline}>{t('mkt.s4.headline')}</h2>
        </div>

        <div className={s.trustBand}>
          {TRUST_PILLARS.map((p) => (
            <div key={p.titleKey} className={s.trustCell}>
              <div className={s.trustIcon}>{p.icon}</div>
              <h3 className={s.trustPillarTitle}>
                {t(p.titleKey)}
                <span className={s.trustPillarSubtitle}>
                  {t(p.subtitleKey)}
                </span>
              </h3>
              <p className={s.trustPillarBody}>{t(p.bodyKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── Section 5 — how it works ──────────────────────────────────────

function Section5HowItWorks({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s5} data-marketing-section="5" id="how">
      <div className={s.s5Inner}>
        <div className={s.s5Header}>
          <h2 className={s.s5Headline}>{t('mkt.s5.headline')}</h2>
        </div>

        <div className={s.steps}>
          <Step num={1} titleKey="mkt.s5.step1.title" bodyKey="mkt.s5.step1.body" t={t} icon={<UploadIcon />} />
          <Step num={2} titleKey="mkt.s5.step2.title" bodyKey="mkt.s5.step2.body" t={t} icon={<ChatIcon />} />
          <Step num={3} titleKey="mkt.s5.step3.title" bodyKey="mkt.s5.step3.body" t={t} icon={<DashboardIcon />} />
        </div>
      </div>
    </section>
  )
}

interface StepProps {
  num: number
  titleKey:
    | 'mkt.s5.step1.title'
    | 'mkt.s5.step2.title'
    | 'mkt.s5.step3.title'
  bodyKey:
    | 'mkt.s5.step1.body'
    | 'mkt.s5.step2.body'
    | 'mkt.s5.step3.body'
  t: ReturnType<typeof useT>
  icon: React.ReactNode
}

function Step({ num, titleKey, bodyKey, t, icon }: StepProps) {
  return (
    <div className={s.step}>
      <div className={s.stepCircle}>{num}</div>
      <h3 className={s.stepTitle}>{t(titleKey)}</h3>
      <p className={s.stepBody}>{t(bodyKey)}</p>
      <div className={s.stepIllust} aria-hidden="true">
        {icon}
      </div>
    </div>
  )
}

// ── Section 6 — pilot CTA ─────────────────────────────────────────

function Section6PilotCta({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <section className={s.s6} data-marketing-section="6" id="pilot">
      <div className={s.s6Inner}>
        <div>
          <h2 className={s.s6Headline}>{t('mkt.s6.headline')}</h2>
          <p className={s.s6Body}>{t('mkt.s6.body')}</p>
          <div className={s.s6Meta}>
            <div className={s.s6MetaItem}>
              <div className={s.s6MetaLabel}>
                {t('mkt.s6.metaPeriodLabel')}
              </div>
              <div className={s.s6MetaValue}>
                {t('mkt.s6.metaPeriodValue')}
              </div>
            </div>
            <div className={s.s6MetaItem}>
              <div className={s.s6MetaLabel}>
                {t('mkt.s6.metaScopeLabel')}
              </div>
              <div className={s.s6MetaValue}>
                {t('mkt.s6.metaScopeValue')}
              </div>
            </div>
          </div>
        </div>

        <PilotForm t={t} />
      </div>
    </section>
  )
}

/**
 * Pilot-inquiry form — wired in #181.
 *
 * Submits to ``POST /api/marketing/pilot-inquiry`` (see
 * ``V3/backend/routers/marketing.py``). The backend sends two
 * transactional emails via Resend: a founder notification to
 * ``contact@eehae.app`` and an acknowledgment to the submitter
 * from ``no-reply@eehae.app``. No DB persistence — the inquiry
 * lives in inboxes only.
 *
 * Spam protection (layered):
 *   - slowapi rate limit (5/hour per IP) — backend side, see
 *     ``rate_limit.py``.
 *   - Honeypot field ``hp_website`` — hidden via the
 *     ``s.honeypot`` CSS class. Bots that auto-fill every input
 *     trip it; the backend silent-200s without sending email.
 *   - Field length caps in the backend Pydantic schema.
 *
 * Consent disclosure: PIPA Art. 15 §2 requires the user to be told
 * what's collected, why, and for how long. The inline note below
 * the submit button does that + links to the full privacy policy.
 * Submitting the form is the consent action.
 */
function PilotForm({ t }: { t: ReturnType<typeof useT> }) {
  const lang = useLang()
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return
    const fd = new FormData(e.currentTarget)
    setSubmitting(true)
    setError(null)
    try {
      await apiPost('/marketing/pilot-inquiry', {
        institution: fd.get('org'),
        contact_name: fd.get('name'),
        role: fd.get('role'),
        email: fd.get('email'),
        phone: fd.get('phone') || null,
        message: fd.get('msg') || null,
        // Pass the current app locale so the acknowledgment email
        // template matches the language the submitter is using.
        locale: lang,
        // Honeypot — should always be empty for real users (the
        // field is visually + a11y hidden via s.honeypot). Bots
        // that auto-fill every input fill it; backend silent-200s
        // without sending email.
        hp_website: fd.get('hp_website') || null,
      })
      setSubmitted(true)
    } catch (err: unknown) {
      setError(
        (err instanceof Error && err.message) ||
          t('mkt.s6.form.errorGeneric'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className={`${s.pilotForm} ${s.pilotFormSuccess}`}>
        <div className={s.pfSuccessIcon} aria-hidden="true">✓</div>
        <h4>{t('mkt.s6.form.successTitle')}</h4>
        <p>{t('mkt.s6.form.successBody')}</p>
      </div>
    )
  }

  return (
    <form className={s.pilotForm} ref={formRef} onSubmit={handleSubmit} noValidate>
      <div className={s.pfTitle}>{t('mkt.s6.formTitle')}</div>
      <div className={s.pfHint}>{t('mkt.s6.formHint')}</div>
      <div className={s.pfGrid}>
        <div className={`${s.pfField} ${s.pfFieldFull}`}>
          <label htmlFor="pf-org">{t('mkt.s6.form.org')}</label>
          <input id="pf-org" name="org" type="text" placeholder={t('mkt.s6.form.orgPlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-name">{t('mkt.s6.form.name')}</label>
          <input id="pf-name" name="name" type="text" placeholder={t('mkt.s6.form.namePlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-role">{t('mkt.s6.form.role')}</label>
          <input id="pf-role" name="role" type="text" placeholder={t('mkt.s6.form.rolePlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-email">{t('mkt.s6.form.email')}</label>
          <input id="pf-email" name="email" type="email" placeholder={t('mkt.s6.form.emailPlaceholder')} required />
        </div>
        <div className={s.pfField}>
          <label htmlFor="pf-phone">
            {t('mkt.s6.form.phone')}{' '}
            <span className={s.pfOptional}>{t('mkt.s6.form.phoneOptional')}</span>
          </label>
          <input id="pf-phone" name="phone" type="tel" placeholder={t('mkt.s6.form.phonePlaceholder')} />
        </div>
        <div className={`${s.pfField} ${s.pfFieldFull}`}>
          <label htmlFor="pf-msg">{t('mkt.s6.form.msg')}</label>
          <textarea id="pf-msg" name="msg" placeholder={t('mkt.s6.form.msgPlaceholder')} />
        </div>

        {/* Honeypot — visually + a11y hidden via .honeypot. Real
            users never see or interact with this; bots auto-filling
            every form input land here, which the backend uses as a
            "silently drop" signal. The label + autocomplete=off make
            the field look benign to a naive scraper. */}
        <div className={s.honeypot} aria-hidden="true">
          <label htmlFor="pf-hp-website">Website (do not fill)</label>
          <input
            id="pf-hp-website"
            name="hp_website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {error && <div className={s.pfError} role="alert">{error}</div>}
        <button type="submit" className={s.pfSubmit} disabled={submitting}>
          {submitting ? t('mkt.s6.form.submitting') : `${t('mkt.s6.form.submit')} →`}
        </button>

        {/* PIPA Art. 15 §2 inline consent disclosure. The privacy
            policy link opens the full §2 (collection items + retention)
            and §5 (cross-border transfers, including the Resend
            transactional-email processor). */}
        <p className={s.pfConsent}>
          {t('mkt.s6.form.consent')}{' '}
          <Link to="/privacy">{t('mkt.s6.form.consentLink')}</Link>
        </p>
      </div>
    </form>
  )
}

// ── Section 7 — footer ────────────────────────────────────────────

function Section7Footer({ t }: { t: ReturnType<typeof useT> }) {
  // Privacy + cookies links route through the same hrefs PrivacyFooter
  // uses elsewhere (see PrivacyFooter.tsx); the marketing footer
  // explicitly surfaces them rather than relying on the floating
  // link strip alone, since school admins read footers as the formal
  // signal of legal compliance.
  return (
    <footer className={s.s7} data-marketing-section="7">
      <div className={s.s7Inner}>
        <div className={s.s7Top}>
          <div className={s.s7BrandCol}>
            <span className={s.s7Brand}>eehae</span>
            <div className={s.s7Tagline}>{t('mkt.s7.tagline')}</div>
            <div className={s.s7Info}>
              {/* OPERATOR-INFO-PENDING: the previous CPO left the
                  project, so this row is gated by ``false`` until a
                  new CPO is designated (PIPA Art. 31). When that
                  happens, fill in ``mkt.s7.info.cpoValue`` in ko.ts /
                  en.ts and flip the gate to ``true`` (or remove the
                  wrapper). */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <div>
                  <span className={s.s7InfoLabel}>
                    {t('mkt.s7.info.cpoLabel')}
                  </span>{' '}
                  {t('mkt.s7.info.cpoValue')}
                </div>
              )}

              {/* INCORPORATION-PENDING (#182): the JSX below is gated
                  by ``false`` so it stays in the tree but doesn't render.
                  When 사업자등록 completes, change ``false`` to ``true``
                  (or remove the wrapper); replace ``info.bizValueTbd``
                  with the real 사업자등록번호 in i18n. */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <>
                  <div>
                    <span className={s.s7InfoLabel}>
                      {t('mkt.s7.info.companyLabel')}
                    </span>{' '}
                    {t('mkt.s7.info.companyValue')}
                  </div>
                  <div>
                    <span className={s.s7InfoLabel}>
                      {t('mkt.s7.info.bizLabel')}
                    </span>{' '}
                    {/* Replace with real 사업자등록번호 once known. */}
                    {t('mkt.s7.info.bizValueTbd')}
                  </div>
                </>
              )}

              {/* OPERATOR-INFO-PENDING: the registered office address
                  (formerly live per #183) was removed after the
                  previous operator's departure. Gate stays ``false``
                  until a new registered address exists — then fill in
                  ``mkt.s7.info.addressValue`` in ko.ts / en.ts and
                  flip the gate to ``true``. Independent of #182 — the
                  address can ship before 사업자등록 completes. */}
              {/* eslint-disable-next-line no-constant-binary-expression */}
              {false && (
                <div>
                  <span className={s.s7InfoLabel}>
                    {t('mkt.s7.info.addressLabel')}
                  </span>{' '}
                  {t('mkt.s7.info.addressValue')}
                </div>
              )}

              {/* contact@eehae.app provisioned (closes #184). */}
              <div>
                <span className={s.s7InfoLabel}>
                  {t('mkt.s7.info.contactLabel')}
                </span>{' '}
                <a href="mailto:contact@eehae.app">
                  {t('mkt.s7.info.contactValue')}
                </a>
              </div>
            </div>
          </div>

          <FooterCol
            heading={t('mkt.s7.col.product')}
            links={[
              { href: '#problem', label: t('mkt.s7.link.problem') },
              { href: '#audiences', label: t('mkt.s7.link.audiences') },
              { href: '#trust', label: t('mkt.s7.link.trust') },
              { href: '#how', label: t('mkt.s7.link.how') },
            ]}
          />

          <FooterCol
            heading={t('mkt.s7.col.legal')}
            links={[
              // Privacy + cookies anchor + CPO anchor all route to
              // /privacy with the matching #fragment. The marketing
              // footer uses raw <a> tags (not react-router <Link>),
              // so navigation is a hard-load and the browser handles
              // the native fragment scroll. Section ids:
              //   /privacy#cookies → <section id="cookies">
              //   /privacy#cpo     → <section id="cpo">
              { href: '/privacy', label: t('mkt.s7.link.privacy') },
              {
                href: '/privacy#cookies',
                label: t('mkt.s7.link.cookies'),
              },
              { href: '/privacy#cpo', label: t('mkt.s7.link.cpo') },
              // Terms of service — wired in issue #425 item 2.
              { href: '/terms', label: t('mkt.s7.link.terms') },
            ]}
          />

          <FooterCol
            heading={t('mkt.s7.col.contact')}
            links={[
              { href: '#pilot', label: t('mkt.s7.link.pilot') },
              // Press / partnerships link — commented out until we
              // have a real press contact target (probably the same
              // mailbox as #184's contact@eehae.app, OR a dedicated
              // press@eehae.app).
              // { href: 'mailto:press@eehae.app', label: t('mkt.s7.link.press') },
            ]}
          />
        </div>

        <div className={s.s7Bottom}>
          <div>{t('mkt.s7.copyright')}</div>
          <div className={s.s7BottomRight}>
            {/* Reuse the global LanguageSwitcher so the marketing
                footer's language toggle stays in sync with the rest
                of the app's language state. */}
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  )
}

interface FooterColLink {
  href: string | null
  label: string
}

function FooterCol({
  heading,
  links,
}: {
  heading: string
  links: FooterColLink[]
}) {
  return (
    <div className={s.s7Col}>
      <h4>{heading}</h4>
      <ul>
        {links.map((link) => (
          <li key={link.label}>
            {link.href ? (
              <a href={link.href}>{link.label}</a>
            ) : (
              <span className={s.s7LinkDisabled} aria-disabled="true">
                {link.label}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Inline icons (line-icon style, single-color via currentColor) ─

function SchoolIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 21h18" />
      <path d="M5 21V8l7-4 7 4v13" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 11h.01" />
      <path d="M15 11h.01" />
    </svg>
  )
}

function TeacherIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
      <path d="M12 3v3" />
      <path d="M12 18v3" />
    </svg>
  )
}

function HagwonIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 9l10-5 10 5-10 5z" />
      <path d="M6 11v5c2 1.5 4 2 6 2s4-.5 6-2v-5" />
      <path d="M22 9v5" />
    </svg>
  )
}

function ShieldIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 4 L34 10 V20 C34 28 28 34 20 36 C12 34 6 28 6 20 V10 Z" />
      <path d="M14 20 L18 24 L26 16" />
    </svg>
  )
}

function GlobeIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="20" cy="20" r="14" />
      <path d="M6 20 H34" />
      <path d="M20 6 C24 10 26 14 26 20 C26 26 24 30 20 34" />
      <path d="M20 6 C16 10 14 14 14 20 C14 26 16 30 20 34" />
      <circle cx="20" cy="20" r="3" fill="currentColor" />
    </svg>
  )
}

function DocumentIcon() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="8" width="28" height="24" rx="2" />
      <path d="M11 16 H29" />
      <path d="M11 22 H22" />
      <path d="M11 27 H18" />
      <circle cx="27" cy="27" r="3.5" fill="none" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg
      width="80"
      height="60"
      viewBox="0 0 80 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="6" y="6" width="48" height="42" rx="3" fill="#ffffff" />
      <path
        d="M14 16 H42 M14 22 H38 M14 28 H32 M14 34 H40"
        stroke="currentColor"
        opacity="0.4"
      />
      <path
        d="M58 24 L70 24 M64 18 L70 24 L64 30"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg
      width="120"
      height="60"
      viewBox="0 0 120 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="8" y="14" width="46" height="20" rx="10" fill="#ffffff" />
      <path d="M14 22 H40 M14 26 H32" opacity="0.4" />
      <rect x="66" y="28" width="46" height="20" rx="10" fill="#ede9fe" />
      <path d="M72 36 H100 M72 40 H92" opacity="0.4" />
    </svg>
  )
}

function DashboardIcon() {
  return (
    <svg
      width="100"
      height="60"
      viewBox="0 0 100 60"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="6" y="6" width="88" height="48" rx="3" fill="#ffffff" />
      <rect
        x="14"
        y="16"
        width="22"
        height="6"
        rx="1"
        fill="currentColor"
        opacity="0.7"
      />
      <rect
        x="14"
        y="26"
        width="36"
        height="3"
        rx="1"
        fill="currentColor"
        opacity="0.3"
      />
      <rect
        x="14"
        y="32"
        width="28"
        height="3"
        rx="1"
        fill="currentColor"
        opacity="0.3"
      />
      <rect x="60" y="16" width="26" height="32" rx="2" fill="#ede9fe" />
      <path
        d="M64 40 L70 32 L76 36 L82 24"
        stroke="currentColor"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

